"use client";

import { localStyles } from "./wiki-sidebar.styles";

import type {
  WikiPageEntry,
  WikiLoadState,
  WikiPageAddress,
  WikiRefreshHandler,
  WikiLinkRenderer,
} from "../wiki-types";
import { useId, useState, useRef, useMemo, useLayoutEffect } from "react";
import { distinguishEntryLabels } from "./distinguish-entry-labels";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../wiki.styles";
import { Input } from "@softmax/design-system";
import { WikiResourceStatus } from "../wiki-resource-status";

const emptyEntries: readonly WikiPageEntry[] = [];

export function WikiSidebar({
  index,
  wikiPageHref,
  pageSlug,
  onRefresh,
  refreshable = true,
  renderLink,
}: Readonly<{
  index: WikiLoadState<readonly WikiPageEntry[]>;
  wikiPageHref: WikiPageAddress;
  pageSlug: string;
  onRefresh: WikiRefreshHandler;
  refreshable?: boolean;
  renderLink: WikiLinkRenderer;
}>) {
  const fieldId = useId();
  const [filter, setFilter] = useState("");
  const navRef = useRef<HTMLElement | null>(null);
  const [navMinHeight, setNavMinHeight] = useState(0);
  const entries = index.status === "ready" ? index.data : emptyEntries;
  const labels = useMemo(() => distinguishEntryLabels(entries), [entries]);
  const filteredEntries = entries
    .filter((entry) =>
      (labels.get(entry.id) ?? entry.title)
        .toLocaleLowerCase()
        .includes(filter.trim().toLocaleLowerCase()),
    )
    .toSorted((a, b) =>
      (labels.get(a.id) ?? a.title).localeCompare(labels.get(b.id) ?? b.title),
    );
  useLayoutEffect(() => {
    if (navRef.current && !filter.trim() && entries.length > 0) {
      setNavMinHeight(navRef.current.scrollHeight);
    }
  }, [entries, filter]);

  return (
    <aside {...stylex.props(localStyles.sidebar)}>
      <h2 {...stylex.props(styles.eyebrow)}>Pages</h2>
      {index.status === "ready" && (
        <p {...stylex.props(styles.smallMuted)}>
          {filter.trim()
            ? `${filteredEntries.length} of ${entries.length} match`
            : `${entries.length} ${entries.length === 1 ? "page" : "pages"} in this wiki`}
        </p>
      )}
      <label
        htmlFor={`${fieldId}-filter`}
        {...stylex.props(localStyles.filter)}
      >
        <span {...stylex.props(localStyles.srOnly)}>Filter pages</span>
        <Input
          id={`${fieldId}-filter`}
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter pages"
        />
      </label>
      <WikiResourceStatus
        refreshable={refreshable}
        state={index}
        label="pages"
        onRefresh={() => onRefresh("index")}
      />
      <nav
        aria-label="Wiki pages"
        ref={navRef}
        {...stylex.props(
          localStyles.pageList,
          localStyles.navHeight(navMinHeight),
        )}
      >
        {index.status === "ready" && filteredEntries.length === 0 && (
          <p {...stylex.props(styles.smallMuted)}>
            {entries.length === 0
              ? "This wiki has no pages yet."
              : `No page matches “${filter.trim()}”.`}
          </p>
        )}
        {filteredEntries.map((entry) => (
          <span key={entry.id}>
            {renderLink({
              href: wikiPageHref({ pageSlug: entry.slug }),
              "aria-current": entry.slug === pageSlug ? "page" : undefined,
              children: labels.get(entry.id) ?? entry.title,
              ...stylex.props(
                localStyles.pageLink,
                entry.slug === pageSlug && localStyles.activePage,
              ),
            })}
          </span>
        ))}
      </nav>
    </aside>
  );
}
